import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import OAuth2Login from 'react-simple-oauth2-login';

const Login = () => {
  const navigate = useNavigate();
  
  const providers = [
    { name: "GitLab", baseUrl: "https://auth.prompteh.com/authorize", providerParam: "gitlab" },
    { name: "GitHub", baseUrl: "https://auth.prompteh.com/authorize", providerParam: "github" },
    { name: "Google", baseUrl: "https://auth.prompteh.com/authorize", providerParam: "google" },
    { name: "Microsoft", baseUrl: "https://auth.prompteh.com/authorize", providerParam: "entra" },
    { name: "Facebook", baseUrl: "https://auth.prompteh.com/authorize", providerParam: "facebook" },
    { name: "X", baseUrl: "https://auth.prompteh.com/authorize", providerParam: "x" },
  ];

  const onSuccess = useCallback((response) => {
    console.log('Login: Auth success response:', response);
    const { code } = response;

    if (code) {
      console.log('Login: Authorization code received, exchanging for token');
      
      fetch('https://auth.prompteh.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      })
        .then(res => res.json())
        .then(data => {
          if (data.error) {
            throw new Error(data.error_description || data.error || 'Token exchange failed');
          }
          
          console.log('Login: Token received successfully, storing in localStorage');
          localStorage.setItem('auth_token', data.token);
          
          // If we have user info from postMessage, use it directly
          if (response.email) {
            const userData = { email: response.email, tier: 'paid' };
            localStorage.setItem('user', JSON.stringify(userData));
            navigate('/account');
            return;
          }
          
          // Otherwise fetch user data
          return fetch('https://auth.prompteh.com/me', {
            headers: {
              'Authorization': `Bearer ${data.token}`,
            },
          })
          .then(res => res.json())
          .then(userData => {
            localStorage.setItem('user', JSON.stringify(userData));
            navigate('/account');
          });
        })
        .catch(err => {
          console.error('Login authentication error:', err);
          alert(`Login failed: ${err.message}`);
        });
    }
  }, [navigate]);

  const onFailure = useCallback((response) => {
    console.error('Login failure:', response);
    alert(`Login failed: ${response.error_description || response.error || 'An unknown error occurred.'}`);
  }, []);

  // Handle postMessage from popup
  const handleOAuthResponse = useCallback((event) => {
    const allowedOrigins = ['https://auth.prompteh.com'];
    if (!allowedOrigins.includes(event.origin)) return;
    
    if (event.data.type === 'oauth-response') {
      console.log('Login: Received postMessage event:', event.data);
      if (event.data.error) {
        onFailure(event.data);
      } else {
        onSuccess(event.data);
      }
    }
  }, [onFailure, onSuccess]);

  // Attach event listener
  React.useEffect(() => {
    window.addEventListener('message', handleOAuthResponse);
    return () => window.removeEventListener('message', handleOAuthResponse);
  }, [handleOAuthResponse]);

  return (
    <div className="flex flex-col gap-4 max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold mb-4 text-center">Login to Prompteh</h2>
      <p className="text-gray-600 mb-6 text-center">Upgrade to a paid account for premium features without ads.</p>
      
      <div className="flex flex-col gap-4">
        {providers.map(provider => (
          <OAuth2Login
            key={provider.name}
            authorizationUrl={provider.baseUrl}
            responseType="code"
            clientId={process.env[`REACT_APP_${provider.name.toUpperCase()}_CLIENT_ID`] || `your-${provider.name.toLowerCase()}-client-id`}
            redirectUri="https://app.prompteh.com/callback"
            onSuccess={onSuccess}
            onFailure={onFailure}
            buttonText={`Login with ${provider.name}`}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            isCrossOrigin={true}
            extraParams={{ provider: provider.providerParam }}
          />
        ))}
      </div>
    </div>
  );
};

export default Login;
